import { useState } from 'react';
import { useNotify } from 'react-admin';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { SectionCard } from '../../shared/Panel';
import { dateTimeNoSeconds } from '../../shared/dateTime';
import { fromDateTimeLocalValue, toDateTimeLocalValue } from '../../shared/clientTime';
import { useCompanies } from '../../shared/companies';
import { ContractTestId } from '../../usage/ContractTestId';
import { returnPreOrderToCs, toErrorMessage } from './intakeApi';
import {
  MIN_DECISION_REASON,
  SPOT_CHANNELS,
  VAT_RATES,
  postDecision,
  subjectLabel,
  type DecisionBody,
  type DecisionItem,
  type IntakeDecisions,
} from './intakeDecisionsApi';

/**
 * Lista „Do zrobienia (n)” w oknie kontroli (26.10.2026, makieta p. Mariusza
 * z 21.10 — okno zlecenia Administracji, panel „Do zrobienia”; decyzja
 * użytkownika 26.10.2026; przełączniki `intakeControlDecisions` S-26.10-06
 * i `intakeControlPriceSurcharges` S-26.10-07).
 *
 * Spółka i stawka VAT — „Potwierdź” albo „Inna…” z uzasadnieniem wyjątku;
 * cena — źródło jawne (dokument klienta albo SPOT: kto, kiedy, skąd; cennika
 * nie ma); dodatki — „Potwierdź” albo „Odrzuć” z powodem. Każda decyzja ma
 * autora i czas, historia jest pod listą. Dopóki coś czeka, eksport do TMS
 * jest zablokowany (przycisk w panelu zapisu i odmowa serwera).
 *
 * Co wolno i dlaczego nie — z API; przyciski tylko dla Administracji.
 */

/** Klucz pozycji w identyfikatorach (`surcharge:fuel-5` → `surcharge-fuel-5`). */
export const itemKey = (subject: string) => subject.replace(/[^a-z0-9-]/gi, '-');

const STATE_CHIP: Record<DecisionItem['state'], { label: string; color: 'warning' | 'error' | 'success' }> = {
  open: { label: 'czeka na decyzję', color: 'warning' },
  stale: { label: 'nieaktualna — dane zmieniły się', color: 'error' },
  done: { label: 'podjęta', color: 'success' },
};

type DialogState =
  | { kind: 'company'; item: DecisionItem }
  | { kind: 'vat'; item: DecisionItem }
  | { kind: 'price'; item: DecisionItem; source: 'document' | 'spot' }
  | { kind: 'reject'; item: DecisionItem }
  | { kind: 'return' }
  | null;

/** Opis decyzji: wynik, wartość, źródło ceny, uzasadnienie, kto i kiedy. */
export function decisionText(d: NonNullable<DecisionItem['decision']>): string {
  const parts = [d.outcomeLabel + (d.value ? `: ${d.value}` : '')];
  const det = d.details ?? null;
  if (det?.source === 'document') parts.push(`dokument: ${String(det.document)}`);
  if (det?.source === 'spot') {
    const channel = SPOT_CHANNELS.find((c) => c.id === det.channel)?.name ?? String(det.channel);
    const at = typeof det.agreedAt === 'string' ? dateTimeNoSeconds(det.agreedAt) : '';
    parts.push(`ustalił: ${String(det.agreedBy)}, ${channel}${at ? `, ${at}` : ''}${det.note ? ` (${String(det.note)})` : ''}`);
  }
  if (d.reason) parts.push(`uzasadnienie: ${d.reason}`);
  parts.push(`${d.decidedBy ?? 'nieznany użytkownik'}, ${dateTimeNoSeconds(d.decidedAt)}`);
  return parts.join(' — ');
}

export const IntakeDecisionsPanel = ({
  preOrderId,
  decisions,
  canAct,
  returnToCs,
  onChanged,
}: {
  preOrderId: number;
  decisions: IntakeDecisions;
  /** Administracja (front tylko ukrywa — rozstrzyga API). */
  canAct: boolean;
  /** Zwrot do CS z obiegu (`intakeHandoff`) — „Zwróć do CS: potwierdź SPOT”. */
  returnToCs: { allowed: boolean; refusal: string | null } | null;
  onChanged: () => void;
}) => {
  const notify = useNotify();
  const companies = useCompanies();
  const [dialog, setDialog] = useState<DialogState>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Pola okien (jedno okno naraz).
  const [reason, setReason] = useState('');
  const [companyId, setCompanyId] = useState<number | ''>('');
  const [rate, setRate] = useState('');
  const [docText, setDocText] = useState('');
  const [spotBy, setSpotBy] = useState('');
  const [spotChannel, setSpotChannel] = useState('phone');
  const [spotAt, setSpotAt] = useState('');
  const [spotNote, setSpotNote] = useState('');
  const [question, setQuestion] = useState('');

  const open = (d: NonNullable<DialogState>) => {
    setError(null);
    setReason('');
    setCompanyId('');
    setRate('');
    setDocText('');
    setSpotBy('');
    setSpotChannel('phone');
    setSpotAt(toDateTimeLocalValue(new Date().toISOString()) ?? '');
    setSpotNote('');
    setQuestion('');
    setDialog(d);
  };

  const send = async (body: DecisionBody, done: string) => {
    setBusy(true);
    setError(null);
    try {
      await postDecision(preOrderId, body);
      notify(done, { type: 'success' });
      setDialog(null);
      onChanged();
    } catch (e) {
      setError(toErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const confirm = (item: DecisionItem) =>
    void send({ subject: item.subject, basis: item.basis, outcome: 'confirmed' }, `${item.label}: potwierdzone`);

  const sendReturn = async () => {
    setBusy(true);
    setError(null);
    try {
      await returnPreOrderToCs(preOrderId, question);
      notify('Zwrócone do CS z pytaniem', { type: 'success' });
      setDialog(null);
      onChanged();
    } catch (e) {
      setError(toErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const surcharges = decisions.items.filter((i) => i.kind === 'surcharge');
  const reasonOk = reason.trim().length >= MIN_DECISION_REASON;

  const actions = (item: DecisionItem) => {
    if (!canAct || !decisions.canDecide) return null;
    const key = itemKey(item.subject);
    switch (item.kind) {
      case 'company':
      case 'vat': {
        return (
          <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap' }}>
            <Button
              size="small"
              variant={item.state === 'done' ? 'outlined' : 'contained'}
              disabled={busy || !item.canConfirm}
              title={item.confirmRefusal ?? undefined}
              data-testid={item.kind === 'company' ? 'adm-zlecenie-decyzja-spolka' : 'adm-zlecenie-decyzja-vat'}
              onClick={() => confirm(item)}
            >
              Potwierdź
            </Button>
            <Button
              size="small"
              variant="outlined"
              disabled={busy}
              data-testid={`intake-decision-${key}-other`}
              onClick={() => open({ kind: item.kind as 'company' | 'vat', item })}
            >
              Inna…
            </Button>
          </Stack>
        );
      }
      case 'price':
        return (
          <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap' }}>
            <Button
              size="small"
              variant="outlined"
              disabled={busy || !item.canConfirm}
              title={item.confirmRefusal ?? undefined}
              data-testid="intake-decision-price-document"
              onClick={() => open({ kind: 'price', item, source: 'document' })}
            >
              Z dokumentu klienta…
            </Button>
            <Button
              size="small"
              variant="outlined"
              disabled={busy || !item.canConfirm}
              title={item.confirmRefusal ?? undefined}
              data-testid="adm-zlecenie-cena-spot"
              onClick={() => open({ kind: 'price', item, source: 'spot' })}
            >
              SPOT…
            </Button>
            <ContractTestId id="adm-zlecenie-cena-cennik" disabled tip={decisions.pricelistNote}>
              <Button size="small" variant="outlined" disabled data-testid="intake-decision-price-pricelist">
                Z cennika
              </Button>
            </ContractTestId>
            {returnToCs ? (
              <Button
                size="small"
                variant="text"
                disabled={busy || !returnToCs.allowed}
                title={returnToCs.refusal ?? undefined}
                data-testid="adm-zlecenie-cena-zwrot-cs"
                onClick={() => {
                  open({ kind: 'return' });
                  setQuestion(
                    item.current
                      ? `Cena ${item.current}: skąd jest ta stawka? Jeśli to uzgodniony SPOT — kto go ustalił, kiedy i jak (telefon, mail)?`
                      : 'W zleceniu nie ma ceny. Jaka jest uzgodniona stawka (SPOT) — kto ją ustalił, kiedy i jak?',
                  );
                }}
              >
                Zwróć do CS: potwierdź SPOT
              </Button>
            ) : null}
          </Stack>
        );
      case 'surcharge':
        return (
          <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap' }}>
            <Button
              size="small"
              variant={item.state === 'done' ? 'outlined' : 'contained'}
              disabled={busy}
              data-testid={`intake-decision-${key}-confirm`}
              onClick={() => confirm(item)}
            >
              Potwierdź
            </Button>
            <Button
              size="small"
              variant="outlined"
              color="warning"
              disabled={busy}
              data-testid={`intake-decision-${key}-reject`}
              onClick={() => open({ kind: 'reject', item })}
            >
              Odrzuć…
            </Button>
          </Stack>
        );
    }
  };

  const row = (item: DecisionItem) => {
    const chip = STATE_CHIP[item.state];
    return (
      <Box
        key={item.subject}
        data-testid={`intake-decision-${itemKey(item.subject)}`}
        data-state={item.state}
        sx={{ borderTop: '1px solid', borderColor: 'divider', pt: 1 }}
      >
        <Stack direction="row" spacing={1} useFlexGap sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {item.state === 'done' ? item.label : item.title}
          </Typography>
          <Chip size="small" color={chip.color} variant="outlined" label={chip.label} />
        </Stack>
        <Typography variant="body2">
          {item.kind === 'vat' ? 'Podpowiedź' : 'Wartość'}: {item.current ?? 'brak'}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          {item.hint}
        </Typography>
        {item.decision ? (
          <Typography
            variant="caption"
            sx={{ display: 'block', color: item.decision.stale ? 'error.main' : 'success.main' }}
            data-testid={`intake-decision-${itemKey(item.subject)}-made`}
          >
            {item.decision.stale ? 'Decyzja nieaktualna (dane zmieniły się po niej): ' : 'Decyzja: '}
            {decisionText(item.decision)}
          </Typography>
        ) : null}
        <Box sx={{ mt: 0.5 }}>{actions(item)}</Box>
      </Box>
    );
  };

  const history = decisions.history;

  return (
    <SectionCard title={`Do zrobienia (${decisions.pending})`}>
      <Stack spacing={1} data-testid="intake-decisions" data-pending={decisions.pending}>
        {decisions.pending === 0 ? (
          <Alert severity="success">Wszystkie decyzje podjęte — można eksportować do TMS.</Alert>
        ) : (
          <Alert severity="warning">
            Eksport do TMS czeka na {decisions.pending === 1 ? 'decyzję' : `decyzje (${decisions.pending})`} Administracji.
          </Alert>
        )}
        {decisions.decideRefusal ? <Alert severity="info">{decisions.decideRefusal}</Alert> : null}
        {error && dialog === null ? (
          <Alert severity="error" onClose={() => setError(null)} data-testid="intake-decision-error">
            {error}
          </Alert>
        ) : null}
        {!canAct && decisions.canDecide ? (
          <Typography variant="caption" color="text.secondary">
            Decyzje podejmuje Administracja.
          </Typography>
        ) : null}
        {decisions.items.filter((i) => i.kind !== 'surcharge').map(row)}
        {surcharges.length > 0 ? (
          <ContractTestId id="adm-zlecenie-dodatki" block>
            <Stack spacing={1} data-testid="intake-decisions-surcharges">
              {surcharges.map(row)}
            </Stack>
          </ContractTestId>
        ) : decisions.priceAndSurcharges ? (
          <Typography variant="caption" color="text.secondary" data-testid="intake-decisions-no-surcharges">
            Dodatki: brak na prezleceniu (korekta paliwowa kontrahenta, czynność wózkowa). Katalogu dodatków klienta
            jeszcze nie ma.
          </Typography>
        ) : null}
        {history.length > 0 ? (
          <Box component="details" data-testid="intake-decisions-history" sx={{ mt: 1 }}>
            <summary data-testid="intake-decisions-history-toggle">Historia decyzji ({history.length})</summary>
            <Stack spacing={0.5} sx={{ mt: 0.5 }}>
              {history.map((h) => (
                <Typography
                  key={h.id}
                  variant="caption"
                  color={h.supersededAt ? 'text.secondary' : 'text.primary'}
                  sx={{ display: 'block' }}
                >
                  {dateTimeNoSeconds(h.decidedAt)} · {h.decidedBy ?? 'nieznany użytkownik'} ·{' '}
                  {subjectLabel(h.subject, decisions.items)}: {h.outcomeLabel}
                  {h.value ? ` — ${h.value}` : ''}
                  {h.reason ? ` — ${h.reason}` : ''}
                  {h.supersededAt ? ' (zastąpiona)' : ''}
                </Typography>
              ))}
            </Stack>
          </Box>
        ) : null}
      </Stack>

      <Dialog open={dialog !== null} onClose={() => (busy ? undefined : setDialog(null))} fullWidth maxWidth="sm">
        {dialog?.kind === 'company' ? (
          <>
            <DialogTitle>Spółka: wyjątek dla tego zlecenia</DialogTitle>
            <DialogContent>
              <Stack spacing={2} sx={{ pt: 1 }}>
                <DialogContentText>
                  Spółka zmieni się w zleceniu i prezleceniu (to ona idzie do pliku dla TMS). Reguła klienta się nie
                  zmienia. Zatwierdzony zapis wraca do szkicu.
                </DialogContentText>
                <TextField
                  select
                  size="small"
                  label="Spółka"
                  value={companyId}
                  onChange={(e) => setCompanyId(Number(e.target.value))}
                  slotProps={{ htmlInput: { 'data-testid': 'intake-decision-company-pick' } }}
                >
                  {companies
                    .filter((c) => c.name !== dialog.item.current)
                    .map((c) => (
                      <MenuItem key={c.id} value={c.id}>
                        {c.name}
                        {c.country ? ` (${c.country})` : ''}
                      </MenuItem>
                    ))}
                </TextField>
                <TextField
                  size="small"
                  label="Uzasadnienie (wymagane)"
                  placeholder="np. dyspozycja handlowca"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  helperText={`co najmniej ${MIN_DECISION_REASON} znaków`}
                  slotProps={{ htmlInput: { 'data-testid': 'intake-decision-reason' } }}
                />
              </Stack>
            </DialogContent>
          </>
        ) : null}
        {dialog?.kind === 'vat' ? (
          <>
            <DialogTitle>Stawka VAT: inna niż podpowiedź</DialogTitle>
            <DialogContent>
              <Stack spacing={2} sx={{ pt: 1 }}>
                <DialogContentText>
                  Decyzja zostaje w historii zlecenia. Stawkę i tak wpisuje się w TMS.
                </DialogContentText>
                <TextField
                  select
                  size="small"
                  label="Stawka VAT"
                  value={rate}
                  onChange={(e) => setRate(e.target.value)}
                  slotProps={{ htmlInput: { 'data-testid': 'intake-decision-vat-pick' } }}
                >
                  {VAT_RATES.filter((r) => r !== dialog.item.current).map((r) => (
                    <MenuItem key={r} value={r}>
                      {r}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  size="small"
                  label="Uzasadnienie (wymagane)"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  helperText={`co najmniej ${MIN_DECISION_REASON} znaków`}
                  slotProps={{ htmlInput: { 'data-testid': 'intake-decision-reason' } }}
                />
              </Stack>
            </DialogContent>
          </>
        ) : null}
        {dialog?.kind === 'price' ? (
          <>
            <DialogTitle>Źródło ceny {dialog.item.current ?? ''}</DialogTitle>
            <DialogContent>
              <Stack spacing={2} sx={{ pt: 1 }}>
                <RadioGroup
                  row
                  value={dialog.source}
                  onChange={(e) => setDialog({ ...dialog, source: e.target.value as 'document' | 'spot' })}
                >
                  <FormControlLabel value="document" control={<Radio size="small" />} label="Dokument klienta" />
                  <FormControlLabel value="spot" control={<Radio size="small" />} label="Stawka SPOT" />
                  <FormControlLabel value="pricelist" control={<Radio size="small" />} label="Cennik" disabled />
                </RadioGroup>
                <Typography variant="caption" color="text.secondary">
                  {decisions.pricelistNote}
                </Typography>
                {dialog.source === 'document' ? (
                  <TextField
                    size="small"
                    label="Który dokument"
                    placeholder="np. PDF zlecenia klienta, strona 1"
                    value={docText}
                    onChange={(e) => setDocText(e.target.value)}
                    slotProps={{ htmlInput: { 'data-testid': 'intake-decision-price-document-text' } }}
                  />
                ) : (
                  <>
                    <TextField
                      size="small"
                      label="Kto ustalił stawkę"
                      placeholder="np. CS — A. Lis, handlowiec"
                      value={spotBy}
                      onChange={(e) => setSpotBy(e.target.value)}
                      slotProps={{ htmlInput: { 'data-testid': 'intake-decision-spot-by' } }}
                    />
                    <TextField
                      select
                      size="small"
                      label="Skąd"
                      value={spotChannel}
                      onChange={(e) => setSpotChannel(e.target.value)}
                      slotProps={{ htmlInput: { 'data-testid': 'intake-decision-spot-channel' } }}
                    >
                      {SPOT_CHANNELS.map((c) => (
                        <MenuItem key={c.id} value={c.id}>
                          {c.name}
                        </MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      size="small"
                      type="datetime-local"
                      label="Kiedy ustalono"
                      value={spotAt}
                      onChange={(e) => setSpotAt(e.target.value)}
                      slotProps={{ inputLabel: { shrink: true }, htmlInput: { 'data-testid': 'intake-decision-spot-at' } }}
                    />
                    <TextField
                      size="small"
                      label={spotChannel === 'other' ? 'Opis (wymagany przy „inne”)' : 'Opis (opcjonalnie)'}
                      value={spotNote}
                      onChange={(e) => setSpotNote(e.target.value)}
                      slotProps={{ htmlInput: { 'data-testid': 'intake-decision-spot-note' } }}
                    />
                  </>
                )}
              </Stack>
            </DialogContent>
          </>
        ) : null}
        {dialog?.kind === 'reject' ? (
          <>
            <DialogTitle>Odrzuć: {dialog.item.label}</DialogTitle>
            <DialogContent>
              <Stack spacing={2} sx={{ pt: 1 }}>
                <DialogContentText>{dialog.item.hint}</DialogContentText>
                <TextField
                  size="small"
                  label="Powód (wymagany)"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  helperText={`co najmniej ${MIN_DECISION_REASON} znaków`}
                  slotProps={{ htmlInput: { 'data-testid': 'intake-decision-reason' } }}
                />
              </Stack>
            </DialogContent>
          </>
        ) : null}
        {dialog?.kind === 'return' ? (
          <>
            <DialogTitle>Zwróć do CS: potwierdź SPOT</DialogTitle>
            <DialogContent>
              <Stack spacing={2} sx={{ pt: 1 }}>
                <DialogContentText>
                  Prezlecenie wraca do CS z pytaniem; podjęte decyzje zostają.
                </DialogContentText>
                <TextField
                  size="small"
                  multiline
                  minRows={3}
                  label="Pytanie do CS"
                  value={question}
                  onChange={(e) => setQuestion(e.target.value)}
                  slotProps={{ htmlInput: { 'data-testid': 'intake-decision-return-question' } }}
                />
              </Stack>
            </DialogContent>
          </>
        ) : null}
        {error ? (
          <Alert severity="error" sx={{ mx: 3 }} data-testid="intake-decision-dialog-error">
            {error}
          </Alert>
        ) : null}
        <DialogActions>
          <Button onClick={() => setDialog(null)} disabled={busy}>
            Anuluj
          </Button>
          <Button
            variant="contained"
            data-testid="intake-decision-save"
            disabled={
              busy ||
              (dialog?.kind === 'company' && (companyId === '' || !reasonOk)) ||
              (dialog?.kind === 'vat' && (rate === '' || !reasonOk)) ||
              (dialog?.kind === 'reject' && !reasonOk) ||
              (dialog?.kind === 'return' && question.trim().length < 10) ||
              (dialog?.kind === 'price' &&
                (dialog.source === 'document'
                  ? docText.trim().length < 3
                  : spotBy.trim().length < 2 || spotAt === '' || (spotChannel === 'other' && spotNote.trim().length < 5)))
            }
            onClick={() => {
              if (!dialog) return;
              if (dialog.kind === 'return') return void sendReturn();
              const base = { subject: dialog.item.subject, basis: dialog.item.basis };
              if (dialog.kind === 'company') {
                return void send(
                  { ...base, outcome: 'exception', companyId: Number(companyId), reason: reason.trim() },
                  'Spółka: zapisano wyjątek',
                );
              }
              if (dialog.kind === 'vat') {
                return void send({ ...base, outcome: 'exception', rate, reason: reason.trim() }, 'Stawka VAT: zapisano wyjątek');
              }
              if (dialog.kind === 'reject') {
                return void send({ ...base, outcome: 'rejected', reason: reason.trim() }, `${dialog.item.label}: odrzucone`);
              }
              if (dialog.source === 'document') {
                return void send(
                  { ...base, outcome: 'confirmed', priceSource: 'document', document: docText.trim() },
                  'Cena: zapisano źródło',
                );
              }
              return void send(
                {
                  ...base,
                  outcome: 'confirmed',
                  priceSource: 'spot',
                  spotBy: spotBy.trim(),
                  spotChannel,
                  spotAt: fromDateTimeLocalValue(spotAt) ?? spotAt,
                  ...(spotNote.trim() ? { spotNote: spotNote.trim() } : {}),
                },
                'Cena: zapisano źródło (SPOT)',
              );
            }}
          >
            {dialog?.kind === 'return' ? 'Zwróć do CS' : dialog?.kind === 'reject' ? 'Odrzuć' : 'Zapisz decyzję'}
          </Button>
        </DialogActions>
      </Dialog>
    </SectionCard>
  );
};
